// Pago de Clientes · COBRO (pestaña de trabajo, docs/108 B4). Un solo componente, varios modos (como
// las entradas del menú de Random):
//   factura       — cobrar UNA factura (por defecto, su saldo);
//   documentos    — pago a documento: varios documentos, cada uno con su monto;
//   estado-cuenta — un monto que se reparte del documento más antiguo al más nuevo (lo hace el backend);
//   rapido        — por folio: busca la factura y cobra su saldo;
//   anticipo      — saldo a favor del cliente, sin documentos;
//   aplicacion    — aplicar el saldo a favor de un recibo a documentos.
// El backend calcula el saldo real de cada documento: no se cobra de más ni dos veces.
// Formato como «Pagos Generales (Ventas)» de Random (pedido de Rodrigo, 2026-10-14): arriba el
// registro de pagos, abajo el estado de cuenta con Saldo ant. / Abono / Saldo act. por documento.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/cliente';
import type { ClienteEncontrado } from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { entero, filaNueva, medioDe, problemaDe, type Fila } from '../comun/medios';
import { fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import { hoyIso } from '../ventas/comercial';
import {
  CODIGO_MEDIO,
  detallePago,
  type PagoDeDocumento,
  clavesCobranza,
  NOMBRE_TIPO_DTE,
  useDocumentosCxc,
  useRecibos,
  type DocumentoConSaldo,
} from './datos';
import { EditorMedios as EditorMediosLocal } from './EditorMedios';
import { ImprimirCartolaCliente, ImprimirRecibo } from './Impresos';

export type ModoCobro =
  'factura' | 'documentos' | 'estado-cuenta' | 'rapido' | 'anticipo' | 'aplicacion';

// Pedido de Rodrigo (2026-10-16: «tengo 4 puntos que hacen lo mismo»): cobrar factura, pago a
// documento, pago a estado de cuenta y cobro rápido son UNA pantalla, «Cobrar»: varias facturas,
// una pagada completa y otra abonada con lo que queda, o búsqueda directa por folio.
const UNIFICADOS = new Set<ModoCobro>(['factura', 'documentos', 'estado-cuenta', 'rapido']);

const TITULO: Record<ModoCobro, string> = {
  factura: 'Cobrar',
  documentos: 'Cobrar',
  'estado-cuenta': 'Pago a estado de cuenta',
  rapido: 'Cobro rápido',
  anticipo: 'Anticipo de cliente',
  aplicacion: 'Aplicación de pagos',
};

/** «EFV 10.000 + ATB 2.760»: con qué se pagó el documento, sumado por medio. */
function resumirPagos(pagos: readonly PagoDeDocumento[]) {
  const por = new Map<string, number>();
  for (const p of pagos) por.set(p.medio, (por.get(p.medio) ?? 0) + Number(p.monto));
  return [...por]
    .map(([m, v]) => `${CODIGO_MEDIO[m] ?? m} ${pesos(String(v)).replace('$', '')}`)
    .join(' + ');
}

/** Código del documento como en Random. */
const TD_RANDOM: Record<number, string> = {
  33: 'FCV',
  34: 'FEV',
  39: 'BLV',
  41: 'BEV',
  56: 'NDV',
  61: 'NCV',
};

interface Resultado {
  reciboId: string;
  numeroVisible: string;
  total?: string;
  aplicado: string;
  saldoAFavor: string;
}

export function Cobro({
  modo: modoPedido,
  clienteInicial,
}: {
  modo: ModoCobro;
  clienteInicial?: { id: string; razonSocial: string } | undefined;
}) {
  const modo: ModoCobro = UNIFICADOS.has(modoPedido) ? 'documentos' : modoPedido;
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [cliente, setCliente] = useState<{ id: string; razonSocial: string } | null>(
    clienteInicial ?? null,
  );
  const [buscando, setBuscando] = useState(false);
  const [folio, setFolio] = useState('');
  const [folioBuscado, setFolioBuscado] = useState('');
  const [montos, setMontos] = useState<Record<string, string>>({});
  const [montoCuenta, setMontoCuenta] = useState('');
  const [reciboAplicar, setReciboAplicar] = useState('');
  const [filas, setFilas] = useState<Fila[]>([
    { ...filaNueva('', 'TRANSFERENCIA'), fecha: hoyIso() },
  ]);
  const [observacion, setObservacion] = useState('');
  const [hecho, setHecho] = useState<Resultado | null>(null);
  const [imprimir, setImprimir] = useState(false);
  const [imprimirCuenta, setImprimirCuenta] = useState(false);
  const porFolio = useDocumentosCxc(
    empresaId,
    { folio: folioBuscado, abiertos: true },
    modo === 'documentos' && /^\d+$/.test(folioBuscado),
  );
  // Folio + Enter: trae al cliente de esa factura y la deja marcada con su saldo.
  const encontrado = folioBuscado ? porFolio.data?.[0] : undefined;
  useEffect(() => {
    if (!encontrado) return;
    const otroCliente = encontrado.clienteEntidadId !== cliente?.id;
    setCliente({ id: encontrado.clienteEntidadId, razonSocial: encontrado.clienteRazonSocial });
    setMontos((m) => ({ ...(otroCliente ? {} : m), [encontrado.documentoId]: encontrado.saldo }));
    setFolio('');
    setFolioBuscado('');
  }, [encontrado, cliente?.id]);
  const clienteId = cliente?.id;
  const docs = useDocumentosCxc(
    empresaId,
    { clienteEntidadId: clienteId, abiertos: true },
    !!clienteId && modo !== 'anticipo',
  );
  const conSaldo = useRecibos(
    empresaId,
    { clienteEntidadId: clienteId, soloConSaldo: true },
    !!clienteId && modo === 'aplicacion',
  );
  const abiertos = useMemo(() => docs.data ?? [], [docs.data]);

  // Lo que se aplica a cada documento (vacío = no se aplica).
  const aplicaciones = useMemo(() => {
    if (modo === 'estado-cuenta' || modo === 'anticipo') return [];
    return abiertos
      .filter((d) => montos[d.documentoId])
      .map((d) => ({ documentoId: d.documentoId, monto: String(entero(montos[d.documentoId]!)) }))
      .filter((a) => Number(a.monto) > 0);
  }, [abiertos, montos, modo]);
  const totalMedios = filas.reduce((s, f) => s + (entero(f.monto) || 0), 0);
  const totalMarcado = aplicaciones.reduce((s, a) => s + Number(a.monto), 0);
  const deudaTotal = abiertos.reduce((s, d) => s + Number(d.saldo), 0);
  const vencido = abiertos
    .filter((d) => d.diasVencido > 0)
    .reduce((s, d) => s + Number(d.saldo), 0);
  // Pago a estado de cuenta: sin monto escrito se abona todo lo recibido (como Random).
  const aRepartir =
    modo === 'estado-cuenta'
      ? Math.min(montoCuenta.trim() ? entero(montoCuenta) || 0 : totalMedios, totalMedios)
      : 0;
  // El mismo reparto que hace el backend: en el orden de la lista, del más antiguo al más nuevo.
  const reparto = useMemo(() => {
    const m = new Map<string, number>();
    let resto = aRepartir;
    for (const d of abiertos) {
      if (resto <= 0) break;
      const x = Math.min(resto, Number(d.saldo));
      m.set(d.documentoId, x);
      resto -= x;
    }
    return m;
  }, [abiertos, aRepartir]);
  const totalAplicado =
    modo === 'estado-cuenta'
      ? [...reparto.values()].reduce((s, x) => s + x, 0)
      : aplicaciones.reduce((s, a) => s + Number(a.monto), 0);
  const abonoDe = (d: DocumentoConSaldo) =>
    modo === 'estado-cuenta'
      ? (reparto.get(d.documentoId) ?? 0)
      : entero(montos[d.documentoId] ?? '') || 0;
  const problemas = filas.map(problemaDe);
  const excede = abiertos.some(
    (d) => montos[d.documentoId] && entero(montos[d.documentoId]!) > Number(d.saldo),
  );
  const recibo = (conSaldo.data ?? []).find((r) => r.reciboId === reciboAplicar);
  const disponibleParaAsignar =
    modo === 'aplicacion' ? Number(recibo?.saldoAFavor ?? 0) : totalMedios;

  // «Asignar lo recibido»: reparte del documento más antiguo al más nuevo, como Random.
  const asignar = () => {
    let resto = disponibleParaAsignar;
    const n: Record<string, string> = {};
    for (const d of abiertos) {
      if (resto <= 0) break;
      const x = Math.min(resto, Number(d.saldo));
      n[d.documentoId] = String(x);
      resto -= x;
    }
    setMontos(n);
  };

  // Al marcar un documento se le asigna lo que queda de lo recibido (sin pasar su saldo): así se
  // paga una factura completa y la siguiente queda abonada con el resto. Sin monto recibido aún,
  // su saldo completo. El abono se puede corregir a mano.
  const alternar = (d: DocumentoConSaldo) =>
    setMontos((m) => {
      const n = { ...m };
      if (n[d.documentoId] !== undefined) {
        delete n[d.documentoId];
        return n;
      }
      const tope = modo === 'aplicacion' ? Number(recibo?.saldoAFavor ?? 0) : totalMedios;
      const usado = Object.values(n).reduce((s, v) => s + (entero(v) || 0), 0);
      const resto = tope - usado;
      n[d.documentoId] = String(resto > 0 ? Math.min(resto, Number(d.saldo)) : d.saldo);
      return n;
    });

  const valido =
    !!clienteId &&
    !excede &&
    (modo === 'aplicacion'
      ? !!recibo && aplicaciones.length > 0 && totalAplicado <= Number(recibo.saldoAFavor)
      : totalMedios > 0 && problemas.every((p) => !p) && totalAplicado <= totalMedios);

  const registrar = useMutation({
    mutationFn: async () => {
      if (modo === 'aplicacion') {
        const cuerpo = { empresaId, clienteEntidadId: clienteId!, aplicaciones };
        return api.post<Resultado>(`/tesoreria/cobros/${reciboAplicar}/aplicar`, cuerpo, {
          idempotencia: intentos.clave('aplicar', cuerpo),
        });
      }
      const cuerpo = {
        empresaId,
        clienteEntidadId: clienteId!,
        medios: filas.map(medioDe),
        aplicaciones,
        ...(modo === 'estado-cuenta' && totalAplicado > 0 ? { distribuir: String(aRepartir) } : {}),
        ...(observacion.trim() ? { observacion: observacion.trim() } : {}),
      };
      return api.post<Resultado>('/tesoreria/cobros', cuerpo, {
        idempotencia: intentos.clave('cobro', cuerpo),
      });
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      setHecho({
        ...r,
        reciboId: r.reciboId ?? reciboAplicar,
        numeroVisible: r.numeroVisible ?? recibo?.numeroVisible ?? '',
      });
      await qc.invalidateQueries({ queryKey: clavesCobranza.cobranza(empresaId) });
    },
    onError: (e) => intentos.cerrar(e),
  });

  // «Nuevo cobro»: pantalla limpia, sin el cliente anterior (Rodrigo, 2026-10-19).
  const nuevo = () => {
    setHecho(null);
    setCliente(null);
    setBuscando(false);
    setMontos({});
    setMontoCuenta('');
    setFolio('');
    setFolioBuscado('');
    setReciboAplicar('');
    setFilas([{ ...filaNueva('', 'TRANSFERENCIA'), fecha: hoyIso() }]);
    setObservacion('');
  };
  const terminar = useCallback(() => setImprimir(false), []);
  const terminarCuenta = useCallback(() => setImprimirCuenta(false), []);
  const elegir = (c: ClienteEncontrado) => {
    setCliente({ id: c.id, razonSocial: c.razon_social });
    setBuscando(false);
    setMontos({});
  };

  if (!puede('cobranza.cobro.registrar'))
    return (
      <div className="pantalla">
        <p className="aviso-linea tono-aviso">
          Registrar cobros requiere el permiso «Registrar cobros de clientes».
        </p>
      </div>
    );

  return (
    <div className="pantalla cobro-tesoreria" data-pantalla="cobro" data-modo={modo}>
      <header className="encabezado-entidad">
        <span className="ruta-migas">Pago de Clientes › {TITULO[modo]}</span>
        <h2>{TITULO[modo]}</h2>
      </header>
      {hecho ? (
        <section className="resultado-cobro" aria-label="Cobro registrado" role="status">
          <h3>Recibo {hecho.numeroVisible} registrado</h3>
          <p>
            Aplicado a documentos: <b>{pesos(hecho.aplicado)}</b>
            {Number(hecho.saldoAFavor) > 0 && (
              <>
                {' '}
                · Queda a favor del cliente: <b>{pesos(hecho.saldoAFavor)}</b>
              </>
            )}
          </p>
          <div className="acciones-modal">
            <button type="button" onClick={() => setImprimir(true)}>
              Imprimir comprobante
            </button>
            <button type="button" className="primario" autoFocus onClick={nuevo}>
              Nuevo cobro
            </button>
          </div>
          {imprimir && (
            <ImprimirRecibo
              reciboId={hecho.reciboId}
              clienteId={clienteId!}
              nombre={cliente?.razonSocial ?? abiertos[0]?.clienteRazonSocial ?? ''}
              alTerminar={terminar}
            />
          )}
        </section>
      ) : (
        <>
          <section className="paso-cobro" aria-label="Cliente">
            <div className="barra-filtros">
              {modo === 'documentos' && (
                <label className="campo campo-corto">
                  <span className="etiqueta">Folio (Enter)</span>
                  <input
                    aria-label="Folio"
                    inputMode="numeric"
                    placeholder="N° de factura"
                    title="Escribe el folio de la factura y Enter: trae al cliente y la marca"
                    value={folio}
                    onChange={(e) => setFolio(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && folio) {
                        e.preventDefault();
                        setFolioBuscado(folio);
                      }
                    }}
                  />
                  {folioBuscado && porFolio.isSuccess && !porFolio.data.length && (
                    <span className="tono-aviso">Sin factura abierta N° {folioBuscado}</span>
                  )}
                </label>
              )}
              <div className="campo">
                <span className="etiqueta">Cliente</span>
                <button
                  type="button"
                  className="selector-cliente"
                  aria-label="Elegir cliente"
                  autoFocus={!cliente}
                  onClick={() => setBuscando(true)}
                >
                  {cliente ? cliente.razonSocial : 'Buscar cliente (F2)…'}
                </button>
              </div>
              <span className="espaciador" />
              {clienteId && modo !== 'anticipo' && (
                <div className="cifras-cliente">
                  <span className="cifra">
                    <span className="etiqueta">Documentos</span>
                    <b>{abiertos.length}</b>
                  </span>
                  <span className="cifra">
                    <span className="etiqueta">Deuda abierta</span>
                    <b>{pesos(String(deudaTotal))}</b>
                  </span>
                  <span className={`cifra ${vencido > 0 ? 'mala' : ''}`}>
                    <span className="etiqueta">Vencido</span>
                    <b>{pesos(String(vencido))}</b>
                  </span>
                </div>
              )}
              {clienteId && (
                <button type="button" onClick={() => setImprimirCuenta(true)}>
                  Imprimir estado de cuenta
                </button>
              )}
            </div>
          </section>
          {!clienteId && (
            <p className="apagado vacio-centrado">
              Elige el cliente (o escribe el folio de una factura) para ver su deuda y registrar el
              pago.
            </p>
          )}

          {modo === 'aplicacion' && clienteId && (
            <label className="campo">
              <span className="etiqueta">Recibo con saldo a favor</span>
              <select
                aria-label="Recibo a aplicar"
                value={reciboAplicar}
                onChange={(e) => setReciboAplicar(e.target.value)}
              >
                <option value="">Elegir…</option>
                {(conSaldo.data ?? []).map((r) => (
                  <option key={r.reciboId} value={r.reciboId}>
                    {r.numeroVisible} · {fecha(r.creadoEn)} · a favor {pesos(r.saldoAFavor)}
                  </option>
                ))}
              </select>
              {conSaldo.isSuccess && !conSaldo.data.length && (
                <span className="apagado">El cliente no tiene saldo a favor.</span>
              )}
            </label>
          )}

          {modo !== 'aplicacion' && clienteId && (
            <section aria-label="Pago recibido" className="paso-cobro">
              <EditorMediosLocal
                filas={filas}
                alCambiar={setFilas}
                sugerido={modo === 'anticipo' ? undefined : totalMarcado}
              />
              <label className="campo campo-ancho">
                <span className="etiqueta">Observación</span>
                <input
                  aria-label="Observación del cobro"
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value)}
                />
              </label>
            </section>
          )}

          {clienteId && modo !== 'anticipo' && (
            <section aria-label="Estado de cuenta" className="paso-cobro">
              <div className="barra-filtros">
                {modo === 'estado-cuenta' && (
                  <label className="campo campo-corto">
                    <span className="etiqueta">Monto a abonar</span>
                    <input
                      aria-label="Monto a abonar"
                      inputMode="numeric"
                      placeholder={totalMedios ? String(totalMedios) : ''}
                      value={montoCuenta}
                      onChange={(e) => setMontoCuenta(e.target.value)}
                    />
                  </label>
                )}
                <span className="espaciador" />
                {(modo === 'documentos' || modo === 'aplicacion') && (
                  <>
                    <button
                      type="button"
                      disabled={!disponibleParaAsignar}
                      title="Reparte lo recibido del documento más antiguo al más nuevo"
                      onClick={asignar}
                    >
                      Asignar lo recibido
                    </button>
                    <button
                      type="button"
                      disabled={!Object.keys(montos).length}
                      onClick={() => setMontos({})}
                    >
                      Limpiar abonos
                    </button>
                  </>
                )}
              </div>
              {modo === 'estado-cuenta' && (
                <p className="apagado">
                  Se abona del documento más antiguo al más nuevo; lo que sobre queda a favor del
                  cliente. Sin monto, se abona todo lo recibido.
                </p>
              )}
              <table className="grilla compacta grilla-cuenta" aria-label="Documentos por cobrar">
                <thead>
                  <tr>
                    {modo !== 'estado-cuenta' && <th />}
                    <th>TD</th>
                    <th>Número</th>
                    <th>NV</th>
                    <th>F. Emisión</th>
                    <th>F. Venc.</th>
                    <th className="der">Días</th>
                    <th className="der">Valor doc.</th>
                    <th className="der">NC</th>
                    <th className="der">Pagado</th>
                    <th className="der">Saldo ant.</th>
                    <th className="der columna-abono">Abono</th>
                    <th className="der">Saldo act.</th>
                  </tr>
                </thead>
                <tbody>
                  {abiertos.map((d) => {
                    const abono = abonoDe(d);
                    const marcado = modo !== 'estado-cuenta' && montos[d.documentoId] !== undefined;
                    const mal = abono > Number(d.saldo);
                    return (
                      <tr
                        key={d.documentoId}
                        className={`${d.diasVencido > 0 ? 'vencido' : ''} ${abono > 0 ? 'con-abono' : ''}`}
                      >
                        {modo !== 'estado-cuenta' && (
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Pagar ${NOMBRE_TIPO_DTE[d.tipoDte] ?? d.tipoDte} ${d.folio ?? ''}`}
                              checked={marcado}
                              onChange={() => alternar(d)}
                            />
                          </td>
                        )}
                        <td title={NOMBRE_TIPO_DTE[d.tipoDte] ?? String(d.tipoDte)}>
                          {TD_RANDOM[d.tipoDte] ?? d.tipoDte}
                        </td>
                        <td className="mono">{d.folio ?? '—'}</td>
                        <td className="mono">{d.notaNumeroVisible}</td>
                        <td>{fecha(d.fechaEmision)}</td>
                        <td>{fecha(d.fechaVencimiento)}</td>
                        <td className={`der ${d.diasVencido > 0 ? 'tono-malo' : ''}`}>
                          {d.diasVencido || ''}
                        </td>
                        <td className="der">{pesos(d.montoTotal)}</td>
                        <td
                          className="der"
                          title={
                            Number(d.compensado)
                              ? `Incluye ${pesos(d.compensado ?? '0')} pagados de más en otro documento del cliente (una NC lo rebajó después de pagado)`
                              : undefined
                          }
                        >
                          {Number(d.notasCredito) + Number(d.compensado ?? 0)
                            ? pesos(String(Number(d.notasCredito) + Number(d.compensado ?? 0)))
                            : ''}
                        </td>
                        <td className="der">
                          {Number(d.aplicado) ? (
                            <span
                              className="pagado-con"
                              title={(d.pagos ?? [])
                                .map(
                                  (p) =>
                                    `${p.recibo} ${fecha(p.fecha)} · ${detallePago(p)}: ${pesos(p.monto)}`,
                                )
                                .join('\n')}
                            >
                              {pesos(d.aplicado)}
                              {!!d.pagos?.length && <small>{resumirPagos(d.pagos)}</small>}
                            </span>
                          ) : (
                            ''
                          )}
                        </td>
                        <td className="der">{pesos(d.saldo)}</td>
                        <td className="der columna-abono">
                          {modo === 'estado-cuenta' ? (
                            abono ? (
                              <b>{pesos(String(abono))}</b>
                            ) : (
                              ''
                            )
                          ) : (
                            marcado && (
                              <input
                                className={`celda-num ${mal ? 'con-error' : ''}`}
                                aria-label={`Monto a pagar ${d.folio ?? ''}`}
                                inputMode="numeric"
                                value={montos[d.documentoId]}
                                onChange={(e) =>
                                  setMontos((m) => ({ ...m, [d.documentoId]: e.target.value }))
                                }
                              />
                            )
                          )}
                        </td>
                        <td className={`der ${mal ? 'tono-malo' : ''}`}>
                          <b>{pesos(String(Number(d.saldo) - abono))}</b>
                        </td>
                      </tr>
                    );
                  })}
                  {docs.isSuccess && !abiertos.length && (
                    <tr>
                      <td colSpan={13} className="apagado">
                        'El cliente no tiene documentos con saldo'
                      </td>
                    </tr>
                  )}
                </tbody>
                {abiertos.length > 1 && (
                  <tfoot>
                    <tr>
                      <td colSpan={modo !== 'estado-cuenta' ? 7 : 6}>
                        <b>Totales</b> · {abiertos.length} documentos
                      </td>
                      <td className="der">
                        {pesos(String(abiertos.reduce((s, d) => s + Number(d.montoTotal), 0)))}
                      </td>
                      <td />
                      <td />
                      <td className="der">{pesos(String(deudaTotal))}</td>
                      <td className="der columna-abono">
                        <b>{pesos(String(totalAplicado))}</b>
                      </td>
                      <td className="der">
                        <b>{pesos(String(deudaTotal - totalAplicado))}</b>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </section>
          )}

          {clienteId && (
            <section className="paso-cobro resumen-cobro" aria-label="Resumen del cobro">
              <div className="flujo-cobro">
                {modo !== 'aplicacion' && (
                  <>
                    <span className="caja-cifra">
                      <span className="etiqueta">Recibido</span>
                      <b>{pesos(String(totalMedios))}</b>
                    </span>
                    <span className="flecha" aria-hidden>
                      →
                    </span>
                  </>
                )}
                <span className="caja-cifra">
                  <span className="etiqueta">Aplicado a documentos</span>
                  <b>{pesos(String(totalAplicado))}</b>
                </span>
                {modo !== 'aplicacion' && (
                  <>
                    <span className="flecha" aria-hidden>
                      +
                    </span>
                    <span className={`caja-cifra ${totalMedios > totalAplicado ? 'aviso' : ''}`}>
                      <span className="etiqueta">Queda a favor del cliente</span>
                      <b>{pesos(String(Math.max(0, totalMedios - totalAplicado)))}</b>
                    </span>
                  </>
                )}
                <span className="espaciador" />
                <button
                  type="button"
                  className="primario boton-grande"
                  disabled={!valido || registrar.isPending}
                  onClick={() => registrar.mutate()}
                >
                  {modo === 'aplicacion' ? 'Aplicar' : 'Registrar cobro'}
                </button>
              </div>
              {excede && <p className="tono-malo">Un abono supera el saldo de su documento.</p>}
              {totalAplicado > totalMedios && modo !== 'aplicacion' && (
                <p className="tono-malo">Lo abonado supera lo recibido.</p>
              )}
            </section>
          )}
          {registrar.isError && <PanelError error={registrar.error} />}
        </>
      )}
      {imprimirCuenta && clienteId && (
        <ImprimirCartolaCliente
          clienteId={clienteId}
          nombre={cliente?.razonSocial ?? abiertos[0]?.clienteRazonSocial ?? ''}
          alTerminar={terminarCuenta}
        />
      )}
      {buscando && (
        <BuscadorCliente
          empresaId={empresaId}
          alElegir={elegir}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </div>
  );
}
